/**
 * Convert a webcam screenshot to base64 data URL with 2:3 aspect ratio enforcement
 * @param {Object} webcamRef - React ref to webcam component
 * @returns {Promise<string|null>} Base64 data URL or null if failed
 */
export const captureWebcamImage = async (webcamRef) => {
  if (!webcamRef.current) {
    console.error('Webcam ref not available');
    return null;
  }

  try {
    const imageSrc = webcamRef.current.getScreenshot();
    // Ensure 2:3 aspect ratio for consistent overlay placement
    const croppedImage = await cropToAspectRatio(imageSrc, 2, 3);
    return croppedImage;
  } catch (error) {
    console.error('Error capturing webcam image:', error);
    return null;
  }
};

/**
 * Crop image to specific aspect ratio (2:3 for overlay consistency)
 * @param {string} base64Data - Base64 data URL
 * @param {number} targetWidth - Target aspect ratio width (e.g., 2)
 * @param {number} targetHeight - Target aspect ratio height (e.g., 3)
 * @returns {Promise<string>} Cropped base64 data URL
 */
export const cropToAspectRatio = (base64Data, targetWidth = 2, targetHeight = 3) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const targetRatio = targetWidth / targetHeight;
      const currentRatio = img.width / img.height;

      let sourceWidth = img.width;
      let sourceHeight = img.height;
      let sourceX = 0;
      let sourceY = 0;

      // Crop to target aspect ratio (center crop)
      if (currentRatio > targetRatio) {
        // Image is wider than target - crop sides
        sourceWidth = img.height * targetRatio;
        sourceX = (img.width - sourceWidth) / 2;
      } else if (currentRatio < targetRatio) {
        // Image is taller than target - crop top/bottom
        sourceHeight = img.width / targetRatio;
        sourceY = (img.height - sourceHeight) / 2;
      }

      // Set canvas to maintain high quality while preserving 2:3
      const outputWidth = 1280; // 2:3 ratio
      const outputHeight = 1920;

      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext('2d', { alpha: false });

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, outputWidth, outputHeight
      );

      // Use high quality for best results
      const croppedBase64 = canvas.toDataURL('image/jpeg', 0.98);
      console.log('[CROP] Output size:', (croppedBase64.length / 1024).toFixed(0), 'KB');
      resolve(croppedBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for cropping'));
    };

    img.src = base64Data;
  });
};

/**
 * Convert base64 data URL to Blob
 * @param {string} base64Data - Base64 data URL
 * @returns {Blob} Blob object
 */
export const base64ToBlob = (base64Data) => {
  const parts = base64Data.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
};

/**
 * Download image to device
 * @param {string} base64Data - Base64 data URL
 * @param {string} filename - Filename for download
 */
export const downloadImage = (base64Data, filename = 'photo-booth-memory.jpg') => {
  try {
    const link = document.createElement('a');
    link.href = base64Data;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading image:', error);
  }
};

/**
 * Validate image size and format
 * @param {string} base64Data - Base64 data URL
 * @returns {Object} Validation result with isValid and error message
 */
export const validateImage = (base64Data) => {
  const MAX_SIZE_MB = 10;

  try {
    // Check if it's a valid base64 data URL
    if (!base64Data || !base64Data.startsWith('data:image/')) {
      return {
        isValid: false,
        error: 'Invalid image format'
      };
    }

    // Calculate size in MB
    const sizeInBytes = (base64Data.length * 3) / 4;
    const sizeInMB = sizeInBytes / (1024 * 1024);

    if (sizeInMB > MAX_SIZE_MB) {
      return {
        isValid: false,
        error: `Image size (${sizeInMB.toFixed(2)}MB) exceeds maximum allowed size (${MAX_SIZE_MB}MB)`
      };
    }

    return {
      isValid: true,
      sizeInMB: sizeInMB.toFixed(2)
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Error validating image'
    };
  }
};

/**
 * Resize image if needed (for optimization)
 * @param {string} base64Data - Base64 data URL
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @returns {Promise<string>} Resized base64 data URL
 */
export const resizeImage = (base64Data, maxWidth = 1920, maxHeight = 1080) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;

        if (width > height) {
          width = maxWidth;
          height = width / aspectRatio;
        } else {
          height = maxHeight;
          width = height * aspectRatio;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', { alpha: false });

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, width, height);

      const resizedBase64 = canvas.toDataURL('image/jpeg', 0.98);
      resolve(resizedBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for resizing'));
    };

    img.src = base64Data;
  });
};

/**
 * Parse image ID from URL query parameters
 * @returns {string|null} Image ID or null if not present
 */
export const getImageIdFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('imageId');
};

/**
 * Upscale image to higher resolution for better quality
 * @param {string} base64Data - Base64 data URL
 * @param {number} targetWidth - Target width in pixels
 * @param {number} targetHeight - Target height in pixels
 * @returns {Promise<string>} Upscaled base64 data URL
 */
export const upscaleImage = (base64Data, targetWidth = 2560, targetHeight = 3840) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d', { alpha: false });

      // Enable maximum quality image smoothing for upscaling
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw image at higher resolution
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Use JPEG with high quality (98%) to keep resolution while reducing file size
      const upscaledBase64 = canvas.toDataURL('image/jpeg', 0.98);
      resolve(upscaledBase64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image for upscaling'));
    };

    img.src = base64Data;
  });
};

/**
 * Compress image for efficient upload while maintaining good quality
 * @param {string} base64Data - Base64 image data
 * @param {number} maxWidth - Maximum width (default 1920 for HD quality)
 * @param {number} quality - JPEG quality 0-1 (default 0.85)
 * @returns {Promise<string>} Compressed base64 image
 */
export const compressForUpload = (base64Data, maxWidth = 1920, quality = 0.85) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');

      // Calculate dimensions maintaining aspect ratio
      const aspectRatio = img.width / img.height;
      let width = maxWidth;
      let height = width / aspectRatio;

      // Don't upscale if image is smaller
      if (img.width < maxWidth) {
        width = img.width;
        height = img.height;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d', { alpha: false });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, width, height);

      // Lower quality for upload - still looks great but much smaller file
      const compressed = canvas.toDataURL('image/jpeg', quality);

      console.log('[COMPRESS] Original:', (base64Data.length / 1024 / 1024).toFixed(2), 'MB');
      console.log('[COMPRESS] Compressed:', (compressed.length / 1024 / 1024).toFixed(2), 'MB');
      console.log('[COMPRESS] Reduction:', (100 - (compressed.length / base64Data.length * 100)).toFixed(1), '%');

      resolve(compressed);
    };

    img.onerror = () => reject(new Error('Compression failed'));
    img.src = base64Data;
  });
};

/**
 * Banner mode options for applyImageBanners
 * EXTENSION: Adds extra height to canvas (image size increases)
 * OVERLAY: Draws banners on top of existing image (image size stays same)
 */
export const BANNER_MODE = {
  EXTENSION: 'extension',
  OVERLAY: 'overlay'
};

/**
 * Apply Polaroid-style white frame with branding to an image
 * - White border on all sides
 * - Top bar: Dubai Police logo (right-aligned)
 * - Bottom bar: "#SPEAKOUT" (left) + "نسمعك دائماً#" (right) - green text on white, 29LT Bukra font
 * @param {string} base64Data - Base64 image data
 * @param {string} mode - BANNER_MODE.EXTENSION or BANNER_MODE.OVERLAY
 * @returns {Promise<string>} Base64 image with frame applied
 */
export const applyImageBanners = (base64Data, mode = BANNER_MODE.EXTENSION) => {
  return new Promise((resolve, reject) => {
    const mainImg = new Image();
    const logoImg = new Image();

    let mainLoaded = false;
    let logoLoaded = false;

    const tryComposite = () => {
      if (!mainLoaded || !logoLoaded) return;

      try {
        const canvas = document.createElement('canvas');

        // Polaroid-style frame dimensions (proportional to image width)
        const sideBorder = Math.round(mainImg.width * 0.04); // 4% side borders
        const topBarHeight = Math.round(mainImg.width * 0.12); // 12% top bar (bigger for logo)
        const bottomBarHeight = Math.round(mainImg.width * 0.18); // 18% bottom bar (bigger for text)

        if (mode === BANNER_MODE.EXTENSION) {
          // EXTENSION MODE: Canvas includes white frame around image
          canvas.width = mainImg.width + (sideBorder * 2);
          canvas.height = mainImg.height + topBarHeight + bottomBarHeight;
        } else {
          // OVERLAY MODE: Canvas same size, image is scaled down
          canvas.width = mainImg.width;
          canvas.height = mainImg.height;
        }

        const ctx = canvas.getContext('2d', { alpha: false });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Fill entire canvas with white (frame color)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (mode === BANNER_MODE.EXTENSION) {
          // EXTENSION: Draw image with white frame around it
          ctx.drawImage(mainImg, sideBorder, topBarHeight, mainImg.width, mainImg.height);
        } else {
          // OVERLAY: Scale image to fit within frame areas
          const availableWidth = canvas.width - (sideBorder * 2);
          const availableHeight = canvas.height - topBarHeight - bottomBarHeight;
          const scale = Math.min(availableWidth / mainImg.width, availableHeight / mainImg.height);
          const scaledWidth = mainImg.width * scale;
          const scaledHeight = mainImg.height * scale;
          const imgX = sideBorder + (availableWidth - scaledWidth) / 2;
          const imgY = topBarHeight + (availableHeight - scaledHeight) / 2;
          ctx.drawImage(mainImg, imgX, imgY, scaledWidth, scaledHeight);
        }

        // === TOP BAR: Dubai Police Logo (right-aligned) ===
        const logoMaxHeight = topBarHeight * 0.7;
        const logoScale = logoMaxHeight / logoImg.height;
        const logoWidth = logoImg.width * logoScale;
        const logoHeight = logoMaxHeight;
        const logoX = canvas.width - logoWidth - (sideBorder * 2); // Right aligned
        const logoY = (topBarHeight - logoHeight) / 2;

        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

        // === BOTTOM BAR: "#SPEAKOUT" (left) + "نسمعك دائماً#" (right) - green on white, 29LT Bukra font ===
        const bottomBarY = canvas.height - bottomBarHeight;
        const bottomCenterY = bottomBarY + bottomBarHeight / 2;
        const horizontalPadding = canvas.width * 0.12; // 12% padding for more spacing

        // English text "#SPEAKOUT" - left side, using 29LT Bukra Bold, green, smaller
        const englishFontSize = bottomBarHeight * 0.28; // Smaller text (was 0.35)
        ctx.font = `700 ${englishFontSize}px '29LT Bukra', 'Arial Black', 'Arial', sans-serif`;
        ctx.fillStyle = '#0e734b'; // Dubai Police green
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText('#SPEAKOUT', horizontalPadding, bottomCenterY);

        // Arabic text "نسمعك دائماً#" (RTL with # at end) - right side, using 29LT Bukra Bold, green, smaller
        const arabicFontSize = bottomBarHeight * 0.28; // Smaller text (was 0.35)
        ctx.font = `700 ${arabicFontSize}px '29LT Bukra', 'Dubai', 'Noto Sans Arabic', 'Arial', sans-serif`;
        ctx.fillStyle = '#0e734b'; // Dubai Police green
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText('نسمعك دائماً#', canvas.width - horizontalPadding, bottomCenterY);

        // Return composited image
        const result = canvas.toDataURL('image/jpeg', 0.95);
        console.log('[BANNERS] Applied Polaroid frame in', mode, 'mode');
        resolve(result);
      } catch (error) {
        reject(new Error('Failed to apply frame: ' + error.message));
      }
    };

    mainImg.onload = () => {
      mainLoaded = true;
      tryComposite();
    };

    logoImg.onload = () => {
      logoLoaded = true;
      tryComposite();
    };

    mainImg.onerror = () => reject(new Error('Failed to load main image for frame'));
    logoImg.onerror = () => reject(new Error('Failed to load logo image for frame'));

    mainImg.src = base64Data;
    logoImg.src = '/dubai-police-logo-horizontal.png'; // Logo from public folder
  });
};

/**
 * Apply sticker overlay to an image (accounting for Polaroid frame if present)
 * @param {string} base64Data - Base64 image data
 * @param {Object} sticker - Sticker configuration object
 * @param {string} stickerImageSrc - Sticker image source (imported image)
 * @param {boolean} hasFrame - Whether the image has a Polaroid frame applied
 * @returns {Promise<string>} Base64 image with sticker applied
 */
export const applyStickerOverlay = (base64Data, sticker, stickerImageSrc, hasFrame = true) => {
  return new Promise((resolve, reject) => {
    const mainImg = new Image();
    const stickerImg = new Image();

    let mainLoaded = false;
    let stickerLoaded = false;

    const tryComposite = () => {
      if (!mainLoaded || !stickerLoaded) return;

      try {
        const canvas = document.createElement('canvas');
        canvas.width = mainImg.width;
        canvas.height = mainImg.height;

        const ctx = canvas.getContext('2d', { alpha: false });
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw main image
        ctx.drawImage(mainImg, 0, 0);

        // Calculate frame boundaries if frame is present
        // Frame dimensions must match applyImageBanners function
        let frameLeft = 0;
        let frameTop = 0;
        let frameRight = canvas.width;
        let frameBottom = canvas.height;

        if (hasFrame) {
          // These percentages must match applyImageBanners
          // Original image width before frame = (canvas.width - 2*sideBorder)
          // sideBorder = originalWidth * 0.04, so canvas.width = originalWidth * 1.08
          // Solve: originalWidth = canvas.width / 1.08
          const estimatedOriginalWidth = canvas.width / 1.08;
          const sideBorder = Math.round(estimatedOriginalWidth * 0.04);
          const topBarHeight = Math.round(estimatedOriginalWidth * 0.12);
          const bottomBarHeight = Math.round(estimatedOriginalWidth * 0.18);

          frameLeft = sideBorder;
          frameTop = topBarHeight;
          frameRight = canvas.width - sideBorder;
          frameBottom = canvas.height - bottomBarHeight;
        }

        // Calculate sticker size based on config (relative to image area, not full canvas)
        const imageAreaWidth = frameRight - frameLeft;
        const stickerWidth = imageAreaWidth * sticker.size;
        const stickerHeight = (stickerImg.height / stickerImg.width) * stickerWidth;

        // Calculate position within the image area (not on the frame)
        const padding = imageAreaWidth * 0.03; // 3% padding from image edges
        let x, y;

        switch (sticker.position) {
          case 'top-left':
            x = frameLeft + padding;
            y = frameTop + padding;
            break;
          case 'top-right':
            x = frameRight - stickerWidth - padding;
            y = frameTop + padding;
            break;
          case 'top-center':
            x = frameLeft + (imageAreaWidth - stickerWidth) / 2;
            y = frameTop + padding;
            break;
          case 'bottom-left':
            x = frameLeft + padding;
            y = frameBottom - stickerHeight - padding;
            break;
          case 'bottom-right':
            x = frameRight - stickerWidth - padding;
            y = frameBottom - stickerHeight - padding;
            break;
          case 'bottom-center':
            x = frameLeft + (imageAreaWidth - stickerWidth) / 2;
            y = frameBottom - stickerHeight - padding;
            break;
          case 'left-center':
            x = frameLeft + padding;
            y = frameTop + ((frameBottom - frameTop) - stickerHeight) / 2;
            break;
          case 'right-center':
            x = frameRight - stickerWidth - padding;
            y = frameTop + ((frameBottom - frameTop) - stickerHeight) / 2;
            break;
          default:
            x = frameLeft + padding;
            y = frameTop + padding;
        }

        // Draw sticker with rotation
        const rotation = sticker.rotation || 0;
        if (rotation !== 0) {
          // Save context state
          ctx.save();
          // Move to sticker center
          const centerX = x + stickerWidth / 2;
          const centerY = y + stickerHeight / 2;
          ctx.translate(centerX, centerY);
          // Rotate
          ctx.rotate((rotation * Math.PI) / 180);
          // Draw sticker centered at origin
          ctx.drawImage(stickerImg, -stickerWidth / 2, -stickerHeight / 2, stickerWidth, stickerHeight);
          // Restore context
          ctx.restore();
        } else {
          // No rotation - draw normally
          ctx.drawImage(stickerImg, x, y, stickerWidth, stickerHeight);
        }

        // Return composited image
        const result = canvas.toDataURL('image/jpeg', 0.95);
        console.log('[STICKER] Applied sticker:', sticker.name, 'at', sticker.position);
        resolve(result);
      } catch (error) {
        reject(new Error('Failed to apply sticker: ' + error.message));
      }
    };

    mainImg.onload = () => {
      mainLoaded = true;
      tryComposite();
    };

    stickerImg.onload = () => {
      stickerLoaded = true;
      tryComposite();
    };

    mainImg.onerror = () => reject(new Error('Failed to load main image'));
    stickerImg.onerror = () => reject(new Error('Failed to load sticker image'));

    mainImg.src = base64Data;
    stickerImg.src = stickerImageSrc;
  });
};
